import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import './index.css'

// Extend Window interface
declare global {
  interface Window {
    __APP_BOOT_STATUS?: string;
    __APP_BOOT_TIME?: number;
  }
}

// Track boot status
if (typeof window !== 'undefined') {
  window.__APP_BOOT_STATUS = 'main_tsx_loading';
}

console.log('Main.tsx loading...');
console.log('React version:', React.version);
console.log('Document ready state:', document.readyState);

// Service Worker cleanup for production
const cleanupServiceWorkers = async () => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  
  const BUILD_VERSION = document.querySelector('meta[name="build-version"]')?.getAttribute('content') || 'unknown';
  const LAST_CLEANED_VERSION = localStorage.getItem('sw_cleaned_version');
  
  // Only clean once per build version
  if (LAST_CLEANED_VERSION === BUILD_VERSION) {
    console.log('[SW Cleanup] Already cleaned for version:', BUILD_VERSION);
    return;
  }
  
  try {
    console.log('[SW Cleanup] Starting cleanup for version:', BUILD_VERSION);
    const registrations = await navigator.serviceWorker.getRegistrations();
    
    if (registrations.length > 0) {
      console.log('[SW Cleanup] Found', registrations.length, 'service worker(s)');
      await Promise.all(registrations.map(reg => {
        console.log('[SW Cleanup] Unregistering:', reg.scope);
        return reg.unregister();
      }));
      
      // Also clear caches
      if (window.caches?.keys) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => {
          console.log('[SW Cleanup] Deleting cache:', k);
          return caches.delete(k);
        }));
      }
      
      console.log('[SW Cleanup] ✅ Cleanup complete');
      localStorage.setItem('sw_cleaned_version', BUILD_VERSION);
    } else {
      console.log('[SW Cleanup] No service workers found');
      localStorage.setItem('sw_cleaned_version', BUILD_VERSION);
    }
  } catch (error) {
    console.error('[SW Cleanup] Failed:', error);
  }
};

const renderApp = async () => {
  try {
    if (typeof window !== 'undefined') {
      window.__APP_BOOT_STATUS = 'rendering';
    }
    
    console.log('Starting React app render...');
    const rootElement = document.getElementById('root');
    console.log('Root element found:', !!rootElement);
    if (!rootElement) throw new Error('Root element not found');

    const root = ReactDOM.createRoot(rootElement);
    console.log('React root created successfully');

    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
    console.log('React app rendered successfully');
    
    // Mark as mounted after a brief delay to ensure React has actually mounted
    setTimeout(() => {
      if (typeof window !== 'undefined') {
        window.__APP_BOOT_STATUS = 'mounted';
        console.log('✅ App mounted successfully in', Date.now() - (window.__APP_BOOT_TIME || 0), 'ms');
      }
    }, 100);
  } catch (error) {
    if (typeof window !== 'undefined') {
      window.__APP_BOOT_STATUS = 'error';
    }
    console.error('Failed to render React app:', error);
    // Fallback to vanilla JS rendering with error details
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.innerHTML = `
        <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; font-family: system-ui; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white;">
          <div style="text-align: center; padding: 2rem; max-width: 640px;">
            <h1 style="margin-bottom: 1rem; font-size: 2rem;">PlanWiseNow</h1>
            <p style="margin-bottom: 1rem; opacity: 0.9;">Hubo un error al cargar la aplicación.</p>
            <pre style="text-align:left; white-space:pre-wrap; background: rgba(0,0,0,0.3); padding: 1rem; border-radius: 8px; max-height: 200px; overflow:auto;">${(error as any)?.message || 'Unknown error'}</pre>
            <button onclick="window.location.reload()" 
                    style="background: white; color: #667eea; border: none; padding: 0.5rem 1rem; border-radius: 4px; cursor: pointer; margin-top: 1rem;">
              Recargar página
            </button>
          </div>
        </div>
      `;
    }
  }
};

console.log('About to call renderApp...');

// Main initialization
(async () => {
  try {
    // Handle ?no-sw=1 parameter - force cleanup
    if (location.search.includes('no-sw=1') && 'serviceWorker' in navigator) {
      console.warn('[SW] Force unregister requested via ?no-sw=1');
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));
      
      if (window.caches?.keys) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
      
      console.warn('[SW] ✅ Force cleanup complete');
      localStorage.removeItem('sw_cleaned_version'); // Reset cleanup flag
      
      // Remove ?no-sw=1 from URL and reload
      const url = new URL(window.location.href);
      url.searchParams.delete('no-sw=1');
      window.history.replaceState({}, '', url);
    } else {
      // Normal startup - clean SW once per version
      await cleanupServiceWorkers();
    }
  } catch (e) {
    console.error('[SW] Cleanup failed', e);
  } finally {
    renderApp();
    console.log('renderApp called');
  }
})();